import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, message, Alert, Tooltip, Switch, Select } from "antd";
import {
    FaBuilding,
    FaEnvelope,
    FaPhoneAlt,
    FaMapMarkerAlt,
    FaPlus,
    FaWifi,
    FaCodeBranch
} from "react-icons/fa";
import { IoInformationCircle } from "react-icons/io5";

const { TextArea } = Input;

const ClinicFormModal = ({
    visible,
    onCancel,
    onFinish,
    initialValues,
    loading,
    existingClinics = []
}) => {
    const [form] = Form.useForm();
    const [realTimeErrors, setRealTimeErrors] = useState({});
    const [realTimeWarnings, setRealTimeWarnings] = useState({});
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [hasLocation, setHasLocation] = useState(true);

    // Network status monitoring
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (visible) {
            form.resetFields();
            setRealTimeErrors({});
            setRealTimeWarnings({});
            const locationValue = initialValues?.hasLocation ?? true;
            setHasLocation(locationValue);
            if (initialValues) {
                form.setFieldsValue({ ...initialValues, hasLocation: locationValue });
            } else {
                form.setFieldsValue({ hasLocation: true });
            }
        }
    }, [visible, initialValues, form]);

    const validateClinicName = (value) => {
        if (!value) return { error: null, warning: null };
        const isDuplicate = existingClinics.some(clinic =>
            (clinic.name || '').toLowerCase().trim() === value.toLowerCase().trim() &&
            (!initialValues || clinic.id !== initialValues.id)
        );
        let error = null;
        let warning = null;
        if (value.length < 2) error = 'Clinic name must be at least 2 characters';
        else if (value.length > 100) error = 'Clinic name must be less than 100 characters';
        else if (isDuplicate) warning = 'A clinic with this name already exists';
        return { error, warning };
    };

    const validateEmail = (value) => {
        if (!value) return null;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        if (value.length > 255) return 'Email must be less than 255 characters';
        return null;
    };

    const validatePhone = (value) => {
        if (!value) return null;
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length < 10) return 'Phone number must be at least 10 digits';
        if (digitsOnly.length > 15) return 'Phone number must be less than 15 digits';
        return null;
    };

    const validateAddress = (value) => {
        if (!value) return null;
        if (value.trim().length < 10) return 'Address must be at least 10 characters';
        if (value.length > 500) return 'Address must be less than 500 characters';
        return null;
    };

    const handleFieldChange = (fieldName, value) => {
        let error = null;
        let warning = null;

        switch (fieldName) {
            case 'name':
            case 'nameAr': {
                const v = validateClinicName(value);
                error = v.error;
                warning = v.warning;
                setRealTimeWarnings(prev => ({ ...prev, [fieldName]: warning }));
                break;
            }
            case 'email':
                error = validateEmail(value);
                break;
            case 'phone':
                error = validatePhone(value);
                break;
            case 'address':
            case 'addressAr':
                error = validateAddress(value);
                break;
            default:
                break;
        }

        setRealTimeErrors(prev => ({ ...prev, [fieldName]: error }));
    };

    const formatPhoneNumber = (value) => {
        if (!value) return value;
        const phoneNumber = value.replace(/[^\d]/g, '');
        const len = phoneNumber.length;
        if (len < 4) return phoneNumber;
        if (len < 7) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        form.setFieldsValue({ phone: formatted });
        handleFieldChange('phone', formatted);
    };

    const handleSubmit = async (values) => {
        try {
            const nameValidation = validateClinicName(values.name);
            const hasErrors = nameValidation.error ||
                (hasLocation && (
                    validateEmail(values.email) ||
                    validatePhone(values.phone) ||
                    validateAddress(values.detailedAddress) ||
                    validateAddress(values.detailedAddressAr)
                ));

            if (hasErrors) {
                message.error('Please fix the validation errors before submitting');
                return;
            }

            await onFinish({ ...values, hasLocation });
        } catch (error) {
            console.error('Form submission error:', error);
            if (!navigator.onLine || error.message?.includes('network') || error.message?.includes('fetch')) {
                message.error('Network error: Please check your internet connection and try again');
            } else {
                message.error(error.message || 'Something went wrong. Please try again.');
            }
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!visible) return;
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                form.submit();
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visible, form, onCancel]);

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <FaBuilding className="text-blue-500" />
                    <span>{initialValues ? "Edit Clinic" : "Add New Clinic"}</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={600}
            footer={[
                <Button key="back" onClick={onCancel} size="large" disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={() => form.submit()}
                    size="large"
                    className="bg-primary mt-3"
                    disabled={loading || Object.values(realTimeErrors).some(e => e !== null) || !navigator.onLine}
                    icon={loading ? null : (initialValues ? null : <FaPlus />)}
                >
                    {loading
                        ? (initialValues ? "Updating..." : "Creating...")
                        : (initialValues ? "Update Clinic" : "Create Clinic")
                    }
                </Button>,
            ]}
            destroyOnClose
        >
            {!isOnline && (
                <Alert
                    message="No Internet Connection"
                    description="Please check your internet connection. You cannot save changes while offline."
                    type="error"
                    icon={<FaWifi />}
                    showIcon
                    className="mb-4"
                />
            )}

            {loading && (
                <Alert
                    message={initialValues ? "Updating Clinic..." : "Creating Clinic..."}
                    description="Please wait while we process your request."
                    type="info"
                    showIcon
                    className="mb-4"
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
                size="large"
            >
                {/* Clinic order */}
                <Form.Item
                    name="order"
                    label={
                        <span className="flex items-center gap-2">
                            Clinic Order
                            <Tooltip title="Enter a unique name for your clinic (2-100 characters)">
                                <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                            </Tooltip>
                        </span>
                    }
                    rules={[
                        { required: true, message: 'Please input clinic order!' },

                    ]}

                >
                    <Input
                        prefix={<FaBuilding className="text-gray-400" />}
                        placeholder="Enter clinic order (e.g., 1, 2, 3)"
                        onChange={(e) => handleFieldChange('order', e.target.value)}
                        showCount
                        maxLength={100}
                    />
                </Form.Item>

                <div className="flex gap-2 ">
                    <Form.Item
                        name="name"
                        label={
                            <span className="flex items-center gap-2">
                                Clinic Name
                                <Tooltip title="Enter a unique name for your clinic (2-100 characters)">
                                    <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Please input clinic name!' },
                            {
                                validator: (_, value) => {
                                    const { error } = validateClinicName(value);
                                    return error ? Promise.reject(error) : Promise.resolve();
                                }
                            }
                        ]}
                        validateStatus={realTimeErrors.name ? 'error' : (realTimeWarnings.name ? 'warning' : '')}
                        help={realTimeErrors.name || (realTimeWarnings.name && (
                            <span style={{ color: '#faad14', fontSize: "12px" }}>{realTimeWarnings.name}</span>
                        ))}
                    >
                        <Input
                            prefix={<FaBuilding className="text-gray-400" />}
                            placeholder="Enter clinic name (e.g., City Medical Center)"
                            onChange={(e) => handleFieldChange('name', e.target.value)}
                            showCount
                            maxLength={100}
                        />
                    </Form.Item>
                    {/* Clinic Name Arabic */}
                    <Form.Item
                        name="nameAr"
                        label={
                            <span className="flex items-center gap-2">
                                Clinic Name (Arabic)
                                <Tooltip title="Enter a unique name for your clinic in Arabic (2-100 characters)">
                                    <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Please input clinic name in Arabic!' },
                            {
                                validator: (_, value) => {
                                    const { error } = validateClinicName(value);
                                    return error ? Promise.reject(error) : Promise.resolve();
                                }
                            }
                        ]}
                        validateStatus={realTimeErrors.nameAr ? 'error' : (realTimeWarnings.nameAr ? 'warning' : '')}
                        help={realTimeErrors.nameAr || (realTimeWarnings.nameAr && (
                            <span style={{ color: '#faad14', fontSize: "12px" }}>{realTimeWarnings.nameAr}</span>
                        ))}
                    >
                        <Input
                            prefix={<FaBuilding className="text-gray-400" />}
                            placeholder="أدخل اسم العيادة"
                            onChange={(e) => handleFieldChange('nameAr', e.target.value)}
                            showCount
                            maxLength={100}
                            dir="rtl"
                        />
                    </Form.Item>
                    
                </div>


                {/* Has Location Toggler */}
                <Form.Item name="hasLocation" label="Has its Own Location" >
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <Switch
                            checked={hasLocation}
                            onChange={(checked) => {
                                setHasLocation(checked);
                                form.setFieldsValue({ hasLocation: checked });
                                // Clear errors for fields that are now hidden
                                if (!checked) {
                                    setRealTimeErrors(prev => ({
                                        ...prev,
                                        email: null,
                                        phone: null,
                                        address: null,
                                        addressAr: null,
                                    }));
                                }
                            }}
                            className={hasLocation ? "bg-primary" : ""}
                        />
                        <div>
                            <p className="font-semibold text-gray-700 text-sm">
                                {hasLocation ? "Has Location" : "Branch"}
                            </p>

                        </div>
                    </div>
                </Form.Item>
<Form.Item
    name="defaultReportType"
    label={
        <span className="flex items-center gap-2">
            Default Report Type
            <Tooltip title="Select the default report type for this clinic">
                <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
            </Tooltip>
        </span>
    }
    initialValue={0}
>
    <Select placeholder="Select report type" size="large">
        <Select.Option value={0}>General</Select.Option>
        <Select.Option value={1}>PhysioTherapy</Select.Option>
        <Select.Option value={2}>Lymphedema</Select.Option>
    </Select>
</Form.Item>
                {/* ── Has Location = TRUE ── */}
                {hasLocation && (
                    <>
                        <div className="flex gap-2 ">
                            <Form.Item
                                name="email"
                                label={
                                    <span className="flex items-center gap-2">
                                        Email Address
                                        <Tooltip title="Enter a valid email address for clinic communications">
                                            <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                        </Tooltip>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please input email!' },
                                    {
                                        validator: (_, value) => {
                                            const error = validateEmail(value);
                                            return error ? Promise.reject(error) : Promise.resolve();
                                        }
                                    }
                                ]}
                                validateStatus={realTimeErrors.email ? 'error' : ''}
                                help={realTimeErrors.email}
                                className="w-full"
                            >
                                <Input
                                    prefix={<FaEnvelope className="text-gray-400" />}
                                    placeholder="info@clinic.com"
                                    type="email"
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    showCount
                                    maxLength={255}
                                />
                            </Form.Item>

                            <Form.Item
                                name="phone"
                                label={
                                    <span className="flex items-center gap-2">
                                        Phone Number
                                        <Tooltip title="Enter phone number (10-15 digits). It will be auto-formatted as you type">
                                            <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                        </Tooltip>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please input phone number!' },
                                    {
                                        validator: (_, value) => {
                                            const error = validatePhone(value);
                                            return error ? Promise.reject(error) : Promise.resolve();
                                        }
                                    }
                                ]}
                                validateStatus={realTimeErrors.phone ? 'error' : ''}
                                help={realTimeErrors.phone}
                                className="w-full"
                            >
                                <Input
                                    prefix={<FaPhoneAlt className="text-gray-400" />}
                                    placeholder="123-456-7890"
                                    onChange={handlePhoneChange}
                                />
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="detailedAddress"
                            label={
                                <span className="flex items-center gap-2">
                                    Full Address
                                    <Tooltip title="Enter complete address including street, city, and postal code (minimum 10 characters)">
                                        <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please input address!' },
                                {
                                    validator: (_, value) => {
                                        const error = validateAddress(value);
                                        return error ? Promise.reject(error) : Promise.resolve();
                                    }
                                }
                            ]}
                            validateStatus={realTimeErrors.address ? 'error' : ''}
                            help={realTimeErrors.address}
                        >
                            <TextArea
                                placeholder="Enter full address including street, city, and postal code"
                                rows={2}
                                onChange={(e) => handleFieldChange('address', e.target.value)}
                                showCount
                                maxLength={500}
                            />
                        </Form.Item>

                        <Form.Item
                            name="detailedAddressAr"
                            label={
                                <span className="flex items-center gap-2">
                                    Full Address (Arabic)
                                    <Tooltip title="Enter complete address in Arabic (minimum 10 characters)">
                                        <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please input address in Arabic!' },
                                {
                                    validator: (_, value) => {
                                        const error = validateAddress(value);
                                        return error ? Promise.reject(error) : Promise.resolve();
                                    }
                                }
                            ]}
                            validateStatus={realTimeErrors.addressAr ? 'error' : ''}
                            help={realTimeErrors.addressAr}
                        >
                            <TextArea
                                placeholder="أدخل العنوان الكامل"
                                rows={2}
                                onChange={(e) => handleFieldChange('addressAr', e.target.value)}
                                showCount
                                maxLength={500}
                                dir="rtl"
                            />
                        </Form.Item>
                    </>
                )}

                {/* ── Has Location = FALSE ── */}
                {!hasLocation && (
                    <Form.Item
                        name="branchName"
                        label={
                            <span className="flex items-center gap-2">
                                Branch Name
                                <Tooltip title="Enter the branch or virtual clinic name">
                                    <span className="text-gray-400 cursor-help"><IoInformationCircle /></span>
                                </Tooltip>
                            </span>
                        }
                        rules={[{ required: true, message: 'Please input the branch name!' }]}
                    >
                        <Input
                            prefix={<FaCodeBranch className="text-gray-400" />}
                            placeholder="Enter branch name (e.g., North Branch, Online Consultations)"
                            showCount
                            maxLength={100}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default ClinicFormModal;